import { useEffect, useState, type ReactNode } from "react";
import { Instagram, MessageSquare, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AutomationTagSelector } from "@/components/automations/nodes/AutomationTagSelector";
import {
  DealCommissionFields,
  emptyDealCommission,
  toCommissionPayload,
  type DealCommissionFieldState,
} from "@/components/deals/DealCommissionFields";
import { supabase } from "@/integrations/supabase/client";
import { useDealStages } from "@/hooks/useDealStages";
import { cn } from "@/lib/utils";
import {
  CRM_MESSAGE_CHANNELS,
  providersForCrmMessageChannel,
  type CrmMessageChannel,
} from "@/lib/crm-automation";
import { whatsappIntegrationDisplayLabel, type WhatsAppIntegrationLike } from "@/lib/whatsapp-integration-label";

const NONE_AGENT = "__none__";

export type CrmWizardActions = {
  sendMessage: boolean;
  message: string;
  sendChannel: CrmMessageChannel;
  sendIntegrationId: string;
  addTag: boolean;
  tagId: string;
  tagName: string;
  setAi: boolean;
  aiMode: "pause" | "resume";
  aiAgentId: string;
  aiAgentName: string;
  createDeal: boolean;
  dealTitle: string;
  dealAmount: string;
  dealStageId: string;
  dealStageProbability: number;
  dealNotes: string;
  dealCommission: DealCommissionFieldState;
  updateLead: boolean;
  leadField: string;
  leadValue: string;
};

export const emptyCrmWizardActions: CrmWizardActions = {
  sendMessage: false,
  message: "",
  sendChannel: "whatsapp",
  sendIntegrationId: "",
  addTag: false,
  tagId: "",
  tagName: "",
  setAi: false,
  aiMode: "pause",
  aiAgentId: "",
  aiAgentName: "",
  createDeal: false,
  dealTitle: "",
  dealAmount: "",
  dealStageId: "",
  dealStageProbability: 0,
  dealNotes: "",
  dealCommission: emptyDealCommission,
  updateLead: false,
  leadField: "notes",
  leadValue: "",
};

export function crmActionsReady(a: CrmWizardActions): boolean {
  if (a.sendMessage && a.message.trim() && a.sendChannel) return true;
  if (a.addTag && a.tagId) return true;
  if (a.setAi) return true;
  if (a.createDeal) return true;
  if (a.updateLead && a.leadField.trim() && a.leadValue.trim()) return true;
  return false;
}

type NodeDef = { node_type: string; config: Record<string, unknown>; label: string };

export function crmActionNodeDefs(a: CrmWizardActions): NodeDef[] {
  const nodes: NodeDef[] = [];
  if (a.sendMessage && a.message.trim()) {
    nodes.push({
      node_type: "send_message",
      config: {
        message: a.message.trim(),
        delivery_channel: a.sendChannel,
        ...(a.sendIntegrationId ? { integration_id: a.sendIntegrationId } : {}),
      },
      label: "Enviar mensagem",
    });
  }
  if (a.addTag && a.tagId) {
    nodes.push({
      node_type: "add_tag",
      config: { tag_id: a.tagId, tag_name: a.tagName },
      label: `Tag: ${a.tagName || "adicionar"}`,
    });
  }
  if (a.setAi) {
    nodes.push({
      node_type: "set_ai_agent",
      config: {
        mode: a.aiMode,
        ...(a.aiAgentId ? { agent_id: a.aiAgentId, agent_name: a.aiAgentName } : {}),
      },
      label: a.aiAgentName
        ? `${a.aiMode === "pause" ? "Pausar" : "Reativar"} · ${a.aiAgentName}`
        : a.aiMode === "pause"
          ? "Pausar agente de IA"
          : "Reativar agente de IA",
    });
  }
  if (a.createDeal) {
    nodes.push({
      node_type: "create_deal",
      config: {
        title: a.dealTitle.trim() || "Negociação",
        manual_amount: a.dealAmount.trim() || "0",
        notes: a.dealNotes.trim(),
        ...(a.dealStageId
          ? { stage_id: a.dealStageId, probability: a.dealStageProbability }
          : {}),
        ...toCommissionPayload(a.dealCommission),
      },
      label: "Criar negociação",
    });
  }
  if (a.updateLead && a.leadField.trim() && a.leadValue.trim()) {
    nodes.push({
      node_type: "update_lead",
      config: { field: a.leadField.trim(), value: a.leadValue.trim() },
      label: `Campo: ${a.leadField}`,
    });
  }
  return nodes;
}

const CHANNEL_ICONS = {
  whatsapp: MessageSquare,
  instagram: Instagram,
  messenger: Send,
} as const;

type Conn = { id: string } & NonNullable<WhatsAppIntegrationLike>;

function ActionRow({
  id,
  label,
  hint,
  checked,
  onCheckedChange,
  children,
}: {
  id: string;
  label: string;
  hint: string;
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  children?: ReactNode;
}) {
  return (
    <div className={cn("px-3 py-2.5", checked && "bg-muted/40")}>
      <div className="flex items-center justify-between gap-3">
        <Label htmlFor={id} className="cursor-pointer text-sm font-medium leading-none">
          {label}
        </Label>
        <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} />
      </div>
      {checked && (
        <div className="mt-3 space-y-3 pb-1">
          <p className="text-xs text-muted-foreground">{hint}</p>
          {children}
        </div>
      )}
    </div>
  );
}

export function CrmWizardFields({
  workspaceId,
  value,
  onChange,
}: {
  workspaceId: string;
  value: CrmWizardActions;
  onChange: (next: CrmWizardActions) => void;
}) {
  const [connections, setConnections] = useState<Conn[]>([]);
  const [agents, setAgents] = useState<{ id: string; name: string; display_name: string | null }[]>([]);
  const [members, setMembers] = useState<{ id: string; display_name: string | null }[]>([]);
  const { data: stages = [] } = useDealStages(workspaceId);
  const openStages = stages.filter((s) => !s.is_closed_won && !s.is_closed_lost);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const providers = providersForCrmMessageChannel(value.sendChannel);
      const [intRes, agentRes, memRes] = await Promise.all([
        supabase
          .from("integrations")
          .select("id, instance_identifier, provider, config")
          .eq("workspace_id", workspaceId)
          .eq("status", "active")
          .in("provider", providers)
          .order("created_at"),
        supabase
          .from("agents")
          .select("id, name, display_name")
          .eq("workspace_id", workspaceId)
          .eq("is_active", true)
          .order("name"),
        supabase
          .from("workspace_members")
          .select("id, display_name")
          .eq("workspace_id", workspaceId)
          .order("display_name"),
      ]);
      if (cancelled) return;
      setConnections((intRes.data ?? []) as Conn[]);
      setAgents((agentRes.data ?? []) as { id: string; name: string; display_name: string | null }[]);
      setMembers((memRes.data ?? []) as { id: string; display_name: string | null }[]);
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId, value.sendChannel]);

  useEffect(() => {
    if (!connections.length) return;
    if (value.sendIntegrationId && connections.some((c) => c.id === value.sendIntegrationId)) return;
    onChange({ ...value, sendIntegrationId: connections.length === 1 ? connections[0].id : "" });
    // shortcut: auto-escolhe só quando há uma conexão ativa no canal
    // eslint-disable-next-line react-hooks/exhaustive-deps -- evita loop com onChange instável
  }, [connections, value.sendIntegrationId]);

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold">Ações</h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          Liga o que deve acontecer. A ordem é de cima para baixo.
        </p>
      </div>
      <div className="rounded-lg border divide-y overflow-hidden">
        <ActionRow
          id="crm-send"
          label="Enviar mensagem"
          hint="Envia neste canal. Se o lead ainda não tiver conversa, ela é criada na hora."
          checked={value.sendMessage}
          onCheckedChange={(v) => onChange({ ...value, sendMessage: v })}
        >
          <div className="space-y-1.5">
            <Label className="text-xs">Canal</Label>
            <div className="grid grid-cols-3 gap-1.5">
              {CRM_MESSAGE_CHANNELS.map((ch) => {
                const Icon = CHANNEL_ICONS[ch.value];
                const selected = value.sendChannel === ch.value;
                return (
                  <button
                    key={ch.value}
                    type="button"
                    onClick={() => onChange({ ...value, sendChannel: ch.value, sendIntegrationId: "" })}
                    className={cn(
                      "flex items-center justify-center gap-1.5 h-9 rounded-md border text-xs font-medium transition-colors",
                      selected
                        ? "border-primary bg-primary/10 text-foreground"
                        : "border-border text-muted-foreground hover:border-primary/50 hover:text-foreground",
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {ch.label}
                  </button>
                );
              })}
            </div>
          </div>
          {connections.length > 0 ? (
            <div className="space-y-1.5">
              <Label className="text-xs">Conexão</Label>
              <Select
                value={value.sendIntegrationId || undefined}
                onValueChange={(v) => onChange({ ...value, sendIntegrationId: v })}
              >
                <SelectTrigger><SelectValue placeholder="Qual número ou página" /></SelectTrigger>
                <SelectContent>
                  {connections.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {whatsappIntegrationDisplayLabel(c, c.provider || "Conexão")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">Nenhuma conexão ativa neste canal.</p>
          )}
          <Textarea
            value={value.message}
            onChange={(e) => onChange({ ...value, message: e.target.value })}
            placeholder="Olá! Vi que você avançou no funil..."
            rows={3}
          />
        </ActionRow>
        <ActionRow
          id="crm-tag"
          label="Adicionar tag"
          hint="Aplica a tag no lead."
          checked={value.addTag}
          onCheckedChange={(v) => onChange({ ...value, addTag: v })}
        >
          <AutomationTagSelector
            workspaceId={workspaceId}
            selectedTagId={value.tagId || undefined}
            selectedTagName={value.tagName || undefined}
            onSelect={(tag) => onChange({ ...value, tagId: tag.id, tagName: tag.name })}
          />
        </ActionRow>
        <ActionRow
          id="crm-ai"
          label="Agente de IA"
          hint="Atribui um agente (ou deixa o atual) e pausa ou reativa nas conversas abertas."
          checked={value.setAi}
          onCheckedChange={(v) => onChange({ ...value, setAi: v })}
        >
          <div className="space-y-1.5">
            <Label className="text-xs">Agente</Label>
            <Select
              value={value.aiAgentId || NONE_AGENT}
              onValueChange={(v) => {
                if (v === NONE_AGENT) {
                  onChange({ ...value, aiAgentId: "", aiAgentName: "" });
                  return;
                }
                const agent = agents.find((a) => a.id === v);
                onChange({
                  ...value,
                  aiAgentId: v,
                  aiAgentName: agent?.display_name || agent?.name || "Agente",
                });
              }}
            >
              <SelectTrigger><SelectValue placeholder="Não atribuir" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE_AGENT}>Não atribuir (manter o atual)</SelectItem>
                {agents.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.display_name || a.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Select value={value.aiMode} onValueChange={(v) => onChange({ ...value, aiMode: v as "pause" | "resume" })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pause">Pausar agente</SelectItem>
              <SelectItem value="resume">Reativar agente</SelectItem>
            </SelectContent>
          </Select>
        </ActionRow>
        <ActionRow
          id="crm-deal"
          label="Criar negociação"
          hint="Mesmos campos da tela de nova negociação, sem produto inicial."
          checked={value.createDeal}
          onCheckedChange={(v) => {
            const first = openStages[0];
            onChange({
              ...value,
              createDeal: v,
              ...(v && !value.dealStageId && first
                ? { dealStageId: first.id, dealStageProbability: first.probability }
                : {}),
            });
          }}
        >
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Título</Label>
              <Input
                value={value.dealTitle}
                onChange={(e) => onChange({ ...value, dealTitle: e.target.value })}
                placeholder="Negociação"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Estágio</Label>
              <Select
                value={value.dealStageId || openStages[0]?.id || undefined}
                onValueChange={(v) => {
                  const stage = openStages.find((s) => s.id === v);
                  onChange({
                    ...value,
                    dealStageId: v,
                    dealStageProbability: stage?.probability ?? 0,
                  });
                }}
              >
                <SelectTrigger><SelectValue placeholder="Estágio" /></SelectTrigger>
                <SelectContent>
                  {openStages.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name} ({s.probability}%)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 col-span-2">
              <Label className="text-xs">Valor (R$)</Label>
              <Input
                type="number"
                min={0}
                step={0.01}
                value={value.dealAmount}
                onChange={(e) => onChange({ ...value, dealAmount: e.target.value })}
                placeholder="0,00"
              />
            </div>
          </div>
          <DealCommissionFields
            members={members}
            value={value.dealCommission}
            onChange={(dealCommission) => onChange({ ...value, dealCommission })}
          />
          <div className="space-y-1">
            <Label className="text-xs">Observações</Label>
            <Textarea
              value={value.dealNotes}
              onChange={(e) => onChange({ ...value, dealNotes: e.target.value })}
              rows={2}
            />
          </div>
        </ActionRow>
        <ActionRow
          id="crm-field"
          label="Atualizar campo do lead"
          hint="Grava um campo nativo do CRM."
          checked={value.updateLead}
          onCheckedChange={(v) => onChange({ ...value, updateLead: v })}
        >
          <div className="grid grid-cols-2 gap-2">
            <Select value={value.leadField} onValueChange={(v) => onChange({ ...value, leadField: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="notes">Observações</SelectItem>
                <SelectItem value="email">E-mail</SelectItem>
                <SelectItem value="phone">Telefone</SelectItem>
                <SelectItem value="name">Nome</SelectItem>
              </SelectContent>
            </Select>
            <Input
              value={value.leadValue}
              onChange={(e) => onChange({ ...value, leadValue: e.target.value })}
              placeholder="Novo valor"
            />
          </div>
        </ActionRow>
      </div>
    </div>
  );
}
